import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { FileUploader } from '@/components/forms/FileUploader';
import { WheelLogoPicker } from './WheelLogoPicker';
import { OutlinePicker } from './OutlinePicker';
import { isOutline } from './libraryItems';
import type { StockClassification } from '@/lib/api/types';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';

/**
 * AddXotskiDialog — creates a new `xk-stock` row.
 *
 * Fields mirror the backend entity (`ox-platform/src/apps/xotski/entities/xk-stock.entity.ts`):
 *   name · description · translate (ES) · image · logo · unitCost ·
 *   initialQuantity · classification · rating (1-5) · restockThreshold
 *
 * `companyId` is injected by the caller (StockListPage) from the auth context.
 */

export interface NewXotski {
  name: string;
  description?: string;
  translate?: string;
  image?: string;
  logo?: string;
  /** CH-01: valor de una unidad en USD. `null` borra el valor guardado. */
  unitCost?: number | null;
  initialQuantity?: number;
  classification?: StockClassification;
  rating: number;
  restockThreshold?: number;
}

/** La marca destino del merch y si hace falta confirmarla (más de una marca). */
export interface XotskiDestination {
  name: string;
  confirm: boolean;
}

export interface AddXotskiDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSave: (xotski: NewXotski) => Promise<void> | void;
  loading?: boolean;
  /** When provided the dialog acts as an editor seeded with these values. */
  initial?: Partial<NewXotski> | null;  /** CH-11 — la marca a la que va el merch, nombrada junto a Guardar. */
  destination?: XotskiDestination;
}

/*
 * Classification UI is hidden for now. When re-enabling, restore the JSX
 * block below + the constants here:
 *
 *   const CLASSIFICATIONS: StockClassification[] = ['SEE', 'ENGAGE', 'TRY', 'ACT'];
 *   const CLASSIFICATION_HINT_KEY: Record<StockClassification, string> = {
 *     SEE: 'stock.dialog.classHint.SEE',
 *     ENGAGE: 'stock.dialog.classHint.ENGAGE',
 *     TRY: 'stock.dialog.classHint.TRY',
 *     ACT: 'stock.dialog.classHint.ACT',
 *   };
 */

const RARITY_COLORS = [
  'var(--color-rarity-1)',
  'var(--color-rarity-2)',
  'var(--color-rarity-3)',
  'var(--color-rarity-4)',
  'var(--color-rarity-5)',
] as const;

function rarityColorFor(r: number): string {
  const i = Math.max(1, Math.min(5, Math.round(r))) - 1;
  return RARITY_COLORS[i];
}

// `unitCost` se guarda como texto en el formulario para que «vacío» sea un
// estado real: el campo es opcional y nadie debe verse obligado a poner un
// cero. Se convierte a número (o a `null`) sólo al guardar.
interface FormState {
  name: string;
  description: string;
  translate: string;
  image: string;
  logo: string;
  unitCost: string;
  initialQuantity: number;
  classification: StockClassification | '';
  rating: number;
  restockThreshold: number;
}

const EMPTY: FormState = {
  name: '',
  description: '',
  translate: '',
  image: '',
  logo: '',
  unitCost: '',
  initialQuantity: 100,
  classification: '',
  rating: 3,
  restockThreshold: 0,
};

function seedForm(initial: Partial<NewXotski> | null | undefined): FormState {
  if (!initial) return EMPTY;
  return {
    name: initial.name ?? '',
    description: initial.description ?? '',
    translate: initial.translate ?? '',
    image: initial.image ?? '',
    logo: initial.logo ?? '',
    unitCost: initial.unitCost != null ? String(initial.unitCost) : '',
    initialQuantity: initial.initialQuantity ?? 0,
    classification: initial.classification ?? '',
    rating: initial.rating ?? 3,
    restockThreshold: initial.restockThreshold ?? 0,
  };
}

export function AddXotskiDialog({
  open,
  onOpenChange,
  onSave,
  loading,
  initial,
  destination,
}: AddXotskiDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="scrollbar-thin max-h-[90vh] max-w-[640px] overflow-y-auto p-0">
        {open && (
          <XotskiFormBody
            initial={initial}
            onSave={onSave}
            onCancel={() => onOpenChange(false)}
            loading={loading}
            destination={destination}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The xotski create/edit form body, decoupled from any modal shell so it
 * renders both inside AddXotskiDialog and inside the right-panel form host
 * (see FormPanelHost / XotskiForm). The surrounding shell owns open/close.
 */
export function XotskiFormBody({
  initial,
  onSave,
  onCancel,
  loading,
  destination,
}: {
  initial?: Partial<NewXotski> | null;
  onSave: (xotski: NewXotski) => Promise<void> | void;
  onCancel: () => void;
  loading?: boolean;
  destination?: XotskiDestination;
}) {
  const t = useT();
  const isEdit = Boolean(initial);
  // CH-11 — con más de una marca en el espacio de trabajo, crear exige decir
  // que sí a la marca nombrada. Editar no: el ítem ya es de donde es.
  const needsConfirm = !!destination?.confirm && !isEdit;
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState<FormState>(() => seedForm(initial));
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm(seedForm(initial));
    setUploading(false);
  }, [initial]);

  const [attempted, setAttempted] = useState(false);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
  };

  /**
   * Form errors keyed by field. Only surfaced after the user has tried to
   * submit once, so the dialog isn't yelling validation errors on first
   * render.
   */
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (form.name.trim().length < 2) {
    errors.name = t('stock.dialog.error.nameLen');
  }
  if (form.initialQuantity <= 0) {
    errors.initialQuantity = t('stock.dialog.error.qtyGt0');
  }
  if (!Number.isInteger(form.initialQuantity)) {
    errors.initialQuantity = t('stock.dialog.error.qtyInt');
  }
  // Valor unitario: opcional. Vacío vale; letras o negativos, no.
  const unitCostParsed = parseUnitCost(form.unitCost);
  if (unitCostParsed === 'invalid') {
    errors.unitCost = t('stock.dialog.error.costInvalid');
  } else if (unitCostParsed != null && unitCostParsed < 0) {
    errors.unitCost = t('stock.dialog.error.costNeg');
  }
  if (form.restockThreshold < 0) {
    errors.restockThreshold = t('stock.dialog.error.threshNeg');
  }
  if (form.restockThreshold > form.initialQuantity) {
    errors.restockThreshold = t('stock.dialog.error.threshExceeds');
  }
  if (form.rating < 1 || form.rating > 5) {
    errors.rating = t('stock.dialog.error.ratingRange');
  }

  const valid = Object.keys(errors).length === 0 && !loading && !uploading && (!needsConfirm || confirmed);

  const submit = () => {
    setAttempted(true);
    if (!valid) return;
    onSave({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      translate: form.translate.trim() || undefined,
      image: form.image.trim() || undefined,
      logo: form.logo.trim() || undefined,
      // Vacío → null, para que editar y borrar el valor lo quite de verdad.
      unitCost: unitCostParsed === 'invalid' ? undefined : unitCostParsed,
      initialQuantity: form.initialQuantity,
      // CH-07: Xotski no pide propósitos. El selector SEE/ENGAGE/TRY/ACT
      // sigue oculto y el guardado ya no impone 'SEE': se conserva lo que
      // el ítem tuviera y, si no tenía nada, la columna queda vacía.
      classification: form.classification || undefined,
      rating: form.rating,
      restockThreshold:
        form.restockThreshold > 0 ? form.restockThreshold : undefined,
    });
  };

  return (
        <div className="p-6">
          {/* ── Header ─────────────────────────────────────────── */}
          <header className="mb-5">
            <div
              className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.10em] text-[var(--color-accent-ink)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {isEdit ? t('stock.dialog.editingTitle') : t('stock.dialog.addingTitle')}
            </div>
            <h2
              className="mb-1.5 text-[22px] font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {isEdit
                ? `${t('stock.dialog.updateHeading')} ${initial?.name?.trim() || t('stock.dialog.updateHeadingFallback')}`
                : t('stock.dialog.newHeading')}
            </h2>
            <p className="text-[13px] text-[var(--color-xk-text-muted)]">
              {isEdit
                ? t('stock.dialog.editLead')
                : t('stock.dialog.newLead')}
            </p>
          </header>

          <div className="space-y-4">
            {/* ── Identity ─────────────────────────────────────── */}
            <Section title={t('stock.dialog.section.identity')}>
              <Field label={t('stock.dialog.field.name')} required error={attempted ? errors.name : undefined}>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder={t('stock.dialog.field.namePlaceholder')}
                  autoFocus
                  className={inputCls}
                />
              </Field>

              <Field label={t('stock.dialog.field.translate')}>
                <input
                  type="text"
                  value={form.translate}
                  onChange={(e) => set('translate', e.target.value)}
                  placeholder={t('stock.dialog.field.translatePlaceholder')}
                  className={inputCls}
                />
              </Field>

              <Field label={t('stock.dialog.field.description')}>
                <textarea
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder={t('stock.dialog.field.descriptionPlaceholder')}
                  rows={2}
                  className={`${inputCls} min-h-[72px] resize-y`}
                />
              </Field>
            </Section>

            {/* ── Visuals ──────────────────────────────────────── */}
            <Section title={t('stock.dialog.section.visuals')}>
              <div className={pairGrid}>
                <Field label={t('stock.dialog.field.productImage')}>
                  <FileUploader
                    value={form.image || null}
                    onChange={(v) => set('image', v ?? '')}
                    onUploadingChange={setUploading}
                    preset="photo"
                  />
                  {/* CH-14 — silueta de la biblioteca como respaldo, nunca como meta. */}
                  <OutlinePicker value={form.image || null} onPick={(url) => set('image', url)} />
                  {isOutline(form.image) && (
                    <p className="mt-1.5 rounded-md border border-[var(--color-warn)] bg-[rgb(234_179_8/0.08)] px-2 py-1.5 text-[11px] text-[var(--color-fg-muted)]">
                      {t('stock.outline.placeholderHint')}
                    </p>
                  )}
                </Field>
                <Field label={t('stock.dialog.field.wheelLogo')}>
                  {/* Free-form upload + curated library picker. Ops drop
                      new logos into ox-platform/uploads/wheel-logos/ and they
                      appear automatically — no rebuild needed. */}
                  <WheelLogoPicker
                    value={form.logo || null}
                    onChange={(v) => set('logo', v ?? '')}
                    onUploadingChange={setUploading}
                  />
                </Field>
              </div>
            </Section>

            {/* ── Inventory ────────────────────────────────────── */}
            <Section title={t('stock.dialog.section.inventory')}>
              <div className={pairGrid}>
                <Field
                  label={t('stock.dialog.field.initialQty')}
                  required
                  error={attempted ? errors.initialQuantity : undefined}
                >
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={form.initialQuantity}
                    onChange={(e) =>
                      set('initialQuantity', Math.max(0, parseInt(e.target.value || '0', 10)))
                    }
                    className={inputCls}
                  />
                </Field>
                <Field
                  label={t('stock.dialog.field.restockThreshold')}
                  error={attempted ? errors.restockThreshold : undefined}
                >
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={form.restockThreshold}
                    onChange={(e) =>
                      set('restockThreshold', Math.max(0, parseInt(e.target.value || '0', 10)))
                    }
                    placeholder={t('stock.dialog.field.restockPlaceholder')}
                    className={inputCls}
                  />
                </Field>
                <Field
                  label={t('stock.dialog.field.unitCost')}
                  hint={t('stock.dialog.field.unitCostHint')}
                  error={attempted ? errors.unitCost : undefined}
                >
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs font-bold text-[var(--color-fg-subtle)]">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={form.unitCost}
                      onChange={(e) => set('unitCost', e.target.value)}
                      onBlur={() => {
                        const v = parseUnitCost(form.unitCost);
                        if (typeof v === 'number') set('unitCost', v.toFixed(2));
                      }}
                      placeholder="0.00"
                      aria-invalid={attempted && !!errors.unitCost}
                      className={cn(inputCls, 'pl-7')}
                    />
                  </div>
                </Field>
              </div>
            </Section>

            {/* Classification section is hidden for now — every xotski gets
                'SEE' by default in `submit()` above. To re-expose, restore
                the JSX block and remove the default in submit(). */}

            {/* ── Rarity ───────────────────────────────────────── */}
            <Section title={t('stock.dialog.section.rarity')}>
              <Field label={t('stock.dialog.field.rarityTier')}>
                {/* Two rows on purpose: the panel is 384px wide, and tiers +
                    badge + bar on one line squeezed the bar to nothing. */}
                <div className="space-y-3 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-bg-surface)] p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex gap-1.5">
                      {[1, 2, 3, 4, 5].map((r) => {
                        const sel = form.rating === r;
                        return (
                          <button
                            key={r}
                            type="button"
                            onClick={() => set('rating', r)}
                            className={`h-8 w-8 rounded-md text-sm font-extrabold transition-colors ${
                              sel
                                ? 'border-2 border-[var(--color-accent-ink)] bg-[var(--color-accent-12)] text-[var(--color-xk-lavender)]'
                                : 'border border-[var(--color-xk-border)] text-[var(--color-xk-lavender)] hover:border-[var(--color-accent-ink)]'
                            }`}
                            style={{ fontFamily: 'var(--font-display)' }}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                    <RarityBadge rating={form.rating} />
                  </div>
                  <RarityBar rating={form.rating} />
                </div>
              </Field>
              <p className="text-[11px] leading-relaxed text-[var(--color-xk-text-muted)]">
                {t('stock.dialog.weight.weightOn')}{' '}
                <span className="font-bold text-[var(--color-xk-lavender)]">
                  {Math.pow(6 - form.rating, 2)}
                </span>{' '}
                · {t('stock.dialog.weight.hint')}
              </p>
            </Section>
          </div>

          {/* ── Validation summary (after first submit attempt) ──── */}
          {attempted && Object.keys(errors).length > 0 && (
            <div className="mt-4 rounded-lg border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 p-3 text-xs">
              <p className="mb-1 font-bold text-[var(--color-error)]">
                {Object.keys(errors).length === 1
                  ? t('stock.dialog.fixFieldsOne')
                  : t('stock.dialog.fixFieldsMany').replace(
                      '{count}',
                      String(Object.keys(errors).length),
                    )}
              </p>
              <ul className="list-disc pl-5 text-[var(--color-fg-primary)]">
                {Object.entries(errors).map(([k, msg]) => (
                  <li key={k}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          {/* CH-11 — a qué marca va. Junto a Guardar, no sólo en la miga de
              arriba, que es donde nadie mira antes de pulsar. */}
          {destination && (
            <div
              data-testid="xotski-destination"
              className="mt-5 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-xs"
            >
              <p className="font-semibold text-[var(--color-fg-primary)]">
                {t(isEdit ? 'stock.dialog.destination.edit' : 'stock.dialog.destination.create', { brand: destination.name })}
              </p>
              {needsConfirm && (
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-[var(--color-fg-muted)]">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="h-4 w-4 accent-[var(--color-xk-accent)]"
                  />
                  {t('stock.dialog.destination.confirm', { brand: destination.name })}
                </label>
              )}
            </div>
          )}

          {/* ── Footer ─────────────────────────────────────────── */}
          <div className="mt-5 flex justify-end gap-3 border-t border-[var(--color-xk-border)] pt-5">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[var(--color-xk-border)] px-5 py-2.5 text-[13px] font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
            >
              {t('stock.dialog.cancel')}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={loading || uploading || (needsConfirm && !confirmed)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2.5 text-[13px] font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading
                ? t('stock.dialog.saving')
                : isEdit
                  ? t('stock.dialog.saveChanges')
                  : t('stock.dialog.addToInventory')}
            </button>
          </div>
        </div>
  );
}

// ── Pieces ──────────────────────────────────────────────────────────────

const inputCls =
  // `--color-bg-input` rather than a white-alpha literal: the literal was a
  // leftover from the dark-only era and renders as a near-invisible tint on
  // the light canvas.
  'block w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-bg-input)] px-3 py-2.5 text-[13px] text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none';

/**
 * Side-by-side field pair.
 *
 * The controls used to sit at different heights whenever one label wrapped and
 * its neighbour didn't — "IMAGEN DEL PRODUCTO" takes two lines, "LOGO EN LA
 * RULETA" one, so the left dropzone started lower than the right one. Reserving
 * two lines for every label in a pair and bottom-aligning the text puts the
 * controls on the same baseline whatever the wording (and whatever the
 * language — the ES strings are longer than the EN ones).
 *
 * Scoped to `> div > label` so it reaches the Field labels only: FileUploader
 * renders its dropzone as a <label> too, and a blanket descendant selector
 * would deform it.
 */
const pairGrid =
  'grid grid-cols-2 gap-3 [&>div>label]:flex [&>div>label]:min-h-[2.1rem] [&>div>label]:items-end';

/**
 * A titled, bordered block. The form previously ran as one flat column with
 * lime labels floating in it, so nothing said where "Visuales" ended and
 * "Inventario" began.
 */
function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)]">
      <h3
        className="border-b border-[var(--color-border-subtle)] px-3.5 py-2.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--color-accent-ink)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {title}
      </h3>
      <div className="space-y-4 p-3.5">{children}</div>
    </section>
  );
}

/** Label + control + error, so the three never drift apart between fields. */
function Field({
  label,
  required,
  error,
  hint,
  children,
}: {
  label: React.ReactNode;
  required?: boolean;
  error?: string;
  /** Una línea bajo el control: para qué sirve el campo cuando no es obvio. */
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        // `tracking-wide`, not `widest`: at 11px uppercase the extra letter
        // spacing made "UMBRAL DE REABASTECIMIENTO" wider than its half-panel
        // column, and it clipped instead of wrapping. `break-words` is the
        // belt-and-braces for whatever long compound a translation adds next.
        className="mb-1.5 block break-words text-[11px] font-extrabold uppercase leading-tight tracking-wide text-[var(--color-xk-text-muted)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        <span>
          {label}
          {required && <span className="ml-1 text-[var(--color-accent-ink)]">*</span>}
        </span>
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-[var(--color-error)]">{error}</p>}
      {!error && hint && <p className="mt-1 text-[11px] leading-snug text-[var(--color-xk-text-muted)]">{hint}</p>}
    </div>
  );
}

/*
 * `ClassificationPill` removed alongside the hidden Classification UI.
 * Re-add it here when the section is re-enabled.
 */

function RarityBadge({ rating }: { rating: number }) {
  const t = useT();
  const tier = Math.max(1, Math.min(5, Math.round(rating)));
  const color = rarityColorFor(rating);
  return (
    <span
      className="inline-block whitespace-nowrap rounded px-2.5 py-[3px] text-[11px] font-black uppercase tracking-wider"
      style={{
        backgroundColor: color,
        // The ramp runs light → dark, so a single ink does not cover it: navy
        // holds 6.6:1 through tier 4 but drops to 3.1:1 on the tier-5 olive.
        // Literals, like the ramp itself: --color-rarity-* are declared only on
        // :root, so the badge looks identical in every theme and a theme-scoped
        // foreground token would drift away from it.
        color: tier >= 5 ? '#f5f5f3' : '#1a1a2e',
        fontFamily: 'var(--font-display)',
      }}
    >
      {t(`stock.card.rarityLabel.${tier}`)}
    </span>
  );
}

function RarityBar({ rating }: { rating: number }) {
  const t = useT();
  const tier = Math.max(1, Math.min(5, Math.round(rating)));
  const color = rarityColorFor(rating);
  const label = t(`stock.card.rarityLabel.${tier}`);
  return (
    <div className="flex flex-1 gap-1" aria-label={`${t('stock.card.rarityTier')} ${label} (${rating}/5)`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="h-1.5 flex-1 rounded-sm"
          style={{
            // The empty half used a 10% white literal, which is invisible on a
            // light canvas — the meter looked like it stopped at the last
            // filled tier instead of showing 3-of-5.
            background: i <= rating ? color : 'var(--color-border-strong)',
          }}
        />
      ))}
    </div>
  );
}

/**
 * «» → null (sin valor). Número con hasta dos decimales → número redondeado.
 * Cualquier otra cosa → 'invalid'. Acepta coma decimal porque el teclado en
 * español la escribe así.
 */
function parseUnitCost(raw: string): number | null | 'invalid' {
  const txt = raw.trim().replace(',', '.');
  if (txt === '') return null;
  if (!/^-?\d+(\.\d{0,2})?$/.test(txt)) return 'invalid';
  return Math.round(Number(txt) * 100) / 100;
}
